import React from 'react';
import PropTypes from 'prop-types';
import { BrowserRouter } from 'react-router-dom';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import NoData from './noData';

import './styles.css';

import { STICKY_MIN_YSCROLL, STICKY_MIN_SCROLL_HEIGHT } from '../../constants';
import MenuNavBar from '../MenuNavBar';
import MastheadContainer from '../Masthead';
import Mapper from '../../utils/data-mapper';
import getRestService from '../../restService.js';
import { decorateAssetsWithThumbnails } from '../../components/MediaCarousel/utils';
import withHostContext from '../../context/host/consumer';
import Loader from '../../components/Loader';

export class MainContainer extends React.Component {
  static defaultProps = {
    scid: -1,
    viewer: { enabledFeatures: [] },
    session: null,
    apiUrl: '',
    isEditMode: false,
    editData: null,
    userData: {},
    applicationStatistics: null,
    deliveryType: null,
    hostContext: {},
  };

  static propTypes = {
    viewer: PropTypes.object,
    session: PropTypes.object,
    scid: PropTypes.number,
    highSchool: PropTypes.object,
    apiUrl: PropTypes.string,
    isEditMode: PropTypes.bool,
    editData: PropTypes.object,
    isFavorite: PropTypes.bool,
    isApplyingTo: PropTypes.bool,
    toggleFavorite: PropTypes.func,
    navigateToNewCollegeProfile: PropTypes.func,
    highschoolCollegeInfo: PropTypes.object,
    userData: PropTypes.object,
    collegeContact: PropTypes.array,
    applicationStatistics: PropTypes.object,
    deliveryType: PropTypes.string,
    clickTrackingEvents: PropTypes.func,
    permissions: PropTypes.object,
  };

  state = {
    collegeData: null,
    statisticsData: null,
    enableSticky: false,
    errorFetchingCollegeData: false,
  };

  isFetchingCollegeData = false;
  isMappingCollegeData = false;
  isFetchingStatisticsData = false;
  isFetchingTestScores = false;
  isFetchingIsEnhanced = false;
  testScoresRestService = undefined;
  restService = null;

  componentDidMount() {
    if (!this.props.isEditMode) {
      window.addEventListener('scroll', this.handleScroll);
    }
    this.getCollegeData();
  }

  /* istanbul ignore next */
  componentWillUnmount() {
    if (!this.props.isEditMode) {
      window.removeEventListener('scroll', this.handleScroll);
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.scid || parseInt(this.props.scid, 10) > 0) {
      this.getCollegeData();
      if (
        this.props.isEditMode &&
        this.isMappingCollegeData &&
        prevProps.editData !== this.props.editData
      ) {
        this.getMappedCollegeData();
      }
    }
  }

  handleScroll = () => {
    /* istanbul ignore next */
    if (
      !this.state.enableSticky &&
      window.scrollY > STICKY_MIN_YSCROLL &&
      document.body.scrollHeight - window.innerHeight > STICKY_MIN_SCROLL_HEIGHT
    ) {
      this.setState({ enableSticky: true });
    } else if (this.state.enableSticky && window.scrollY <= STICKY_MIN_YSCROLL) {
      this.setState({ enableSticky: false });
    }
  };

  getCollegeData() {
    const { featureFlags } = this.props;
    const youVisitToursEnabledForNaviance = get(
      featureFlags,
      'featureMatchHubsYouVisitTours',
    );
    const youVisitToursEnabledForIntersect = get(
      featureFlags,
      'featureMatchHubsIntersectYouVisitTours',
    );
    const youVisitToursEnabled =
      youVisitToursEnabledForNaviance || youVisitToursEnabledForIntersect;

    if (!this.props.scid || parseInt(this.props.scid, 10) === 0) {
      this.setState({ collegeData: {}, errorFetchingCollegeData: true });
    }

    if (
      parseInt(this.props.scid, 10) > 0 &&
      (!this.state.collegeData || !this.state.statisticsData)
    ) {
      const { session, apiUrl } = this.props;
      if (!this.restService) {
        this.restService = getRestService({ session, uri: apiUrl });
      }

      const isCamino = get(this.props, 'hostContext.isCamino', false);

      if (
        !this.props.isEditMode &&
        !this.state.collegeData &&
        !this.isFetchingCollegeData
      ) {
        this.isFetchingCollegeData = true;
        this.fetchCollegeData();
      } else if (!this.isMappingCollegeData) {
        this.isMappingCollegeData = true;
        this.getMappedCollegeData();
      }
      if (!this.state.statisticsData && !this.isFetchingStatisticsData) {
        if (session.authType !== 'Bearer' || isCamino) {
          // Ignore Statistics in Intersect HE/HS/Support
          this.isFetchingStatisticsData = true;
          this.fetchCollegeStatistics();
        }
      }
      if (!isCamino && !this.state.testScores && !this.isFetchingTestScores) {
        this.isFetchingTestScores = true;
        this.fetchTestScores();
      }
      if (!this.state.isEnhanced && !this.isFetchingIsEnhanced && youVisitToursEnabled) {
        this.isFetchingIsEnhanced = true;
        this.fetchIsEnhanced();
      }
    }
  }

  fetchCollegeData() {
    this.restService
      .sendRequest(`/${this.props.scid}`, { method: 'GET' })
      .then((response) => {
        return response.json();
      })
      .then((response) => {
        const mappedProfile = new Mapper(response.fullProfile);
        const nextResponse = {
          ...response,
          fullProfile: mappedProfile.getState(),
        };

        return nextResponse;
      })
      .then(async (response) => {
        const campus_media = get(response, 'webTour.campus_media', {});
        const assets = get(campus_media, 'assets', []);
        const decoratedAssets = await decorateAssetsWithThumbnails(assets);

        return {
          ...response,
          webTour: {
            campus_media: {
              ...campus_media,
              assets: decoratedAssets,
            },
          },
        };
      })
      .then((collegeData) => {
        this.isFetchingCollegeData = false;
        if (!collegeData || collegeData.error) {
          this.setState({ collegeData: {}, errorFetchingCollegeData: true });
        } else {
          this.setState({ collegeData, errorFetchingCollegeData: false });
        }
      })
      .catch((error) => {
        const errorMessage = error.message; //=> String
        console.log(`ERROR: ${errorMessage}`);
        this.setState({ errorFetchingCollegeData: true });
      });
  }

  fetchCollegeStatistics() {
    this.restService
      .sendRequest('/statistics', { method: 'GET' })
      .then((response) => response.json())
      .then((statisticsData) => {
        this.isFetchingStatisticsData = false;
        this.setState({ statisticsData });
      })
      .catch((error) => {
        const errorMessage = error.message; //=> String
        console.log(`ERROR: ${errorMessage}`);
      });
  }

  fetchTestScores() {
    if (!this.testScoresRestService) {
      const { apiUrl, session } = this.props;
      const url = new URL(apiUrl);
      this.testScoresRestService = getRestService({ session, uri: url.origin });
    }
    this.testScoresRestService
      .sendRequest('/test-scores', { method: 'GET' })
      .then((response) => response.json())
      .then((response) => {
        /* istanbul ignore next */
        if (response.message) {
          const errorMessage = response.message; //=> String
          console.log(`TEST SCORES ERROR: ${errorMessage}`);
        } else {
          this.isFetchingTestScores = false;
          this.setState({ testScores: response });
        }
      })
      .catch(
        /* istanbul ignore next */ (error) => {
          const errorMessage = error.message; //=> String
          console.log(`TEST SCORES ERROR: ${errorMessage}`);
        },
      );
  }

  getMappedCollegeData = async () => {
    const { isEditMode, editData } = this.props;
    if (isEditMode && editData) {
      const mappedProfile = new Mapper(editData.fullProfile);
      const assets = get(editData, 'webTour.campus_media.assets', []);
      let decoratedAssets = [];
      /* istanbul ignore else */
      if (assets.length > 0) {
        decoratedAssets = await decorateAssetsWithThumbnails(assets);
      }
      const { webTour, ...rest } = editData;
      const campus_media = get(editData, 'webTour.campus_media', null);
      const collegeDataFromHem = {
        ...rest,
        webTour: {
          campus_media: {
            ...campus_media,
            assets: decoratedAssets,
          },
        },
        fullProfile: mappedProfile.getState(),
      };
      this.setState({ collegeData: collegeDataFromHem });
    }
  };

  fetchIsEnhanced = async () => {
    const { session, apiUrl } = this.props;
    const restService = getRestService({ session, uri: apiUrl });
    restService
      .sendRequest(`/has-enhanced?scids=${this.props.scid}`, { method: 'GET' })
      .then((response) => response.json())
      .then((response) => {
        const { enhanced } = response;
        this.isFetchingIsEnhanced = false;
        this.setState({ isEnhanced: enhanced.length > 0 });
      })
      .catch(
        /* istanbul ignore next */ (error) => {
          const errorMessage = error.message; //=> String
          console.log(`ERROR: ${errorMessage}`);
        },
      );
  };

  render() {
    const {
      statisticsData,
      enableSticky,
      collegeData,
      errorFetchingCollegeData,
      testScores,
      isEnhanced,
    } = this.state;
    const {
      scid,
      isFavorite,
      toggleFavorite,
      isApplyingTo,
      highSchool,
      highschoolCollegeInfo,
      userData,
      collegeContact,
      sendEmail,
      navigateToNewCollegeProfile,
      applicationStatistics,
      apiUrl,
      session,
      isEditMode,
      deliveryType,
      fetchCollegeEvents,
      visits,
      clickTrackingEvents,
      fetchCounselorComments,
      permissions,
      featureFlags,
    } = this.props;
    const isCamino = get(this.props, 'hostContext.isCamino', false);

    const newCollegeProfileEnabled = get(
      featureFlags,
      'featurePersonalizedProfilesCollege',
      false,
    );
    const hiid = get(collegeData, 'fullProfile.overview.hiid.value', null);
    const country = get(collegeData, 'fullProfile.overview.country.value', null);
    const collegeType = get(
      collegeData,
      'fullProfile.overview.schoolType.type.value',
      null,
    );
    const isValidCountry =
      country && ['United States', 'Canada', 'Puerto Rico'].includes(country);
    const isValidCollegeType =
      !collegeType ||
      (collegeType && !['Corporation', 'Graduate School'].includes(collegeType));

    if (hiid && newCollegeProfileEnabled && isValidCountry && isValidCollegeType) {
      navigateToNewCollegeProfile(hiid);
    }
    return (
      <React.Fragment>
        {collegeData && !isEmpty(collegeData) ? (
          <div className="main-container">
            <MastheadContainer
              collegeData={collegeData}
              scid={scid}
              userData={userData}
              collegeContact={collegeContact}
              sendEmail={sendEmail}
              isFavorite={isFavorite}
              isApplyingTo={isApplyingTo}
              toggleFavorite={toggleFavorite}
              collapsed={enableSticky}
            />
            <BrowserRouter>
              <MenuNavBar
                collegeData={collegeData}
                isEnhanced={isEnhanced}
                userData={userData}
                testScores={testScores}
                collegeContact={collegeContact}
                applicationStatistics={applicationStatistics}
                sendEmail={sendEmail}
                tuitionStatistics={get(statisticsData, 'tuitionStatistics')}
                enableSticky={enableSticky}
                highSchool={highSchool}
                highschoolCollegeInfo={highschoolCollegeInfo}
                scid={scid}
                apiUrl={apiUrl}
                session={session}
                isEditMode={isEditMode}
                deliveryType={deliveryType}
                isApplyingTo={isApplyingTo}
                fetchCollegeEvents={fetchCollegeEvents}
                visits={visits}
                clickTrackingEvents={clickTrackingEvents}
                fetchCounselorComments={fetchCounselorComments}
                permissions={permissions}
                featureFlags={featureFlags}
              />
            </BrowserRouter>
          </div>
        ) : (
          <Loader active={isCamino} />
        )}
        {errorFetchingCollegeData && (
          <NoData isCamino={isCamino} getUrls={this.props.hostContext.getUrls} />
        )}
      </React.Fragment>
    );
  }
}

export default withHostContext(MainContainer);
